/**
 * CHI NHÁNH — một tầng trên phòng ban (chốt 24/09/2026): Chi Nhánh Nghệ An gồm các phòng KD phía
 * ngoài, Chi Nhánh Sài Gòn gồm KD phía trong cùng Producer, Sản Xuất AI, Thumbnail.
 *
 * Chi nhánh ở đây chỉ để NHÌN và LỌC: sơ đồ bộ máy tách hai nhánh, thanh «Xem» có thêm một ô, bảng
 * nhân sự gom theo chi nhánh. Nó KHÔNG đổi quyền — ai thấy dữ liệu của ai vẫn do chức vụ và phòng
 * quyết định ở máy chủ. Vì thế danh sách nằm trong bản ghi CÀI ĐẶT (khoá `chiNhanh`, chỉ Giám đốc
 * ghi) chứ không phải trong bảng `org_units`: thêm một tầng vào bảng ấy là đụng vào phân quyền,
 * sơ đồ và luật tạo/xoá đơn vị, quá nhiều rủi ro cho một việc chỉ để nhìn.
 *
 * Phòng KHÔNG nằm trong chi nhánh nào vẫn làm việc bình thường, chỉ là đứng riêng ở mục «Chưa xếp
 * chi nhánh» — công ty mở thêm phòng mà quên xếp thì không ai bị mất dữ liệu.
 */
import type { RecordData } from './records/parsing';

export interface Branch {
  /** Mã chi nhánh — DỮ LIỆU, sinh một lần khi tạo rồi giữ nguyên. */
  id: string;
  name: string;
  /** Mã phòng ban thuộc chi nhánh này. */
  departments: string[];
}

/** Khoá giữ danh sách chi nhánh trong bản ghi `setting`. Là DỮ LIỆU — đừng đổi. */
export const BRANCHES_KEY = 'chiNhanh';

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** Mã phòng luôn quy về chữ thường, cùng cách máy chủ đọc `department_id`. */
const unitId = (value: unknown): string => text(value).toLowerCase();

export function parseBranches(value: unknown): Branch[] {
  if (!Array.isArray(value)) return [];
  const branches: Branch[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const raw = item as Record<string, unknown>;
    const id = unitId(raw.id);
    const name = text(raw.name);
    if (!id || !name || seen.has(id)) continue;
    seen.add(id);
    const departments = Array.isArray(raw.dept) ? raw.dept.map(unitId).filter(Boolean) : [];
    branches.push({ id, name, departments: [...new Set(departments)] });
  }
  return branches;
}

export function serializeBranches(branches: readonly Branch[]): RecordData[] {
  return branches.map((branch) => ({
    id: branch.id,
    name: branch.name,
    dept: branch.departments,
  }));
}

/** Mã mới từ tên («Chi Nhánh Nghệ An» → `cn-nghe-an`), tránh trùng mã đang có. */
export function newBranchId(name: string, existing: readonly Branch[]): string {
  const base =
    name
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/đ/gi, 'd')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 24) || 'cn';
  const taken = new Set(existing.map((branch) => branch.id));
  if (!taken.has(base)) return base;
  for (let index = 2; ; index += 1) {
    const candidate = `${base}-${index}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/** Chi nhánh chứa phòng này; không phòng nào hoặc phòng chưa xếp → null. */
export function branchOfDepartment(
  branches: readonly Branch[],
  departmentId: string | null | undefined,
): Branch | null {
  const id = unitId(departmentId);
  if (!id) return null;
  return branches.find((branch) => branch.departments.includes(id)) ?? null;
}

export const branchName = (branches: readonly Branch[], id: string): string =>
  branches.find((branch) => branch.id === id)?.name ?? id;

/** Mã các phòng của một chi nhánh, dạng tập hợp để hỏi nhanh khi lọc. */
export function departmentsOfBranch(
  branches: readonly Branch[],
  branchId: string,
): ReadonlySet<string> {
  return new Set(branches.find((branch) => branch.id === branchId)?.departments ?? []);
}

/**
 * Thêm / bỏ một phòng khỏi chi nhánh. Một phòng chỉ thuộc MỘT chi nhánh: tích vào chi nhánh này
 * thì tự gỡ khỏi chi nhánh kia, không để một phòng nằm hai nơi rồi bảng cộng hai lần.
 */
export function withDepartmentInBranch(
  branches: readonly Branch[],
  branchId: string,
  departmentId: string,
  on: boolean,
): Branch[] {
  const department = unitId(departmentId);
  return branches.map((branch) => {
    const has = branch.departments.includes(department);
    if (branch.id === branchId) {
      if (on === has) return branch;
      return {
        ...branch,
        departments: on
          ? [...branch.departments, department]
          : branch.departments.filter((id) => id !== department),
      };
    }
    if (!on || !has) return branch;
    return { ...branch, departments: branch.departments.filter((id) => id !== department) };
  });
}

export function withBranchName(
  branches: readonly Branch[],
  branchId: string,
  name: string,
): Branch[] {
  return branches.map((branch) => (branch.id === branchId ? { ...branch, name } : branch));
}

export function withoutBranch(branches: readonly Branch[], branchId: string): Branch[] {
  return branches.filter((branch) => branch.id !== branchId);
}

export interface BranchGroup<T> {
  /** null = nhóm «Chưa xếp chi nhánh», luôn đứng cuối. */
  branch: Branch | null;
  departments: T[];
}

/**
 * Xếp phòng vào chi nhánh, theo đúng thứ tự Giám đốc khai; phòng chưa xếp gom vào nhóm cuối. Chi
 * nhánh không còn phòng nào trong danh sách truyền vào thì không hiện — bảng đang lọc một phòng
 * mà vẫn vẽ tiêu đề chi nhánh rỗng là thừa.
 */
export function groupByBranch<T extends { id: string }>(
  branches: readonly Branch[],
  departments: readonly T[],
): BranchGroup<T>[] {
  const groups: BranchGroup<T>[] = [];
  const used = new Set<string>();
  for (const branch of branches) {
    const inBranch = departments.filter((department) => {
      const hit = branch.departments.includes(department.id);
      if (hit) used.add(department.id);
      return hit;
    });
    if (inBranch.length) groups.push({ branch, departments: inBranch });
  }
  const rest = departments.filter((department) => !used.has(department.id));
  if (rest.length) groups.push({ branch: null, departments: rest });
  return groups;
}
